import { useEffect, useRef } from 'react';
import { CHANNEL_RIGHTS, WINDOW_STATUS, toUTCDate } from './useAlternatePlaybackFlow';

const POLL_INTERVAL_MS = 30_000;

/**
 * Gestiona los timers para los flujos de eventos alternos (WEBCL-2944/2947/2949/2950).
 * Usa polling cada 30s en lugar de setTimeout puro para evitar throttling del browser
 * cuando la pestaña está en background.
 *
 * Los callbacks se almacenan en un ref para evitar capturar versiones stale
 * dentro del setInterval, sin necesidad de incluirlos en el array de dependencias.
 *
 * @param {object} params
 * @param {object|null}   params.channel             - Canal activo resuelto por useAlternatePlaybackFlow
 * @param {string|null}   params.rights              - CHANNEL_RIGHTS.*
 * @param {string|null}   params.windowStatus        - WINDOW_STATUS.*
 * @param {string|null}   params.scheduledAt         - ISO8601 inicio del partido
 * @param {boolean}       params.shouldCutAtScheduledStart - Corta alternos al iniciar el partido
 * @param {object}        params.miniplayerConfig     - { playLiveMatch, playComments }
 * @param {function}      params.onStartLivePlayback  - Callback: iniciar reproducción de partido en vivo
 * @param {function}      params.onStartAlternate     - Callback: iniciar reproducción del canal alterno
 * @param {function}      params.onHideMiniPlayer     - Callback: ocultar miniplayer
 * @param {function}      params.onShowCommercialOffer- Callback: mostrar oferta comercial
 * @param {function}      params.onRefetchAlternate   - Callback: re-consultar API v2
 */
export const useAlternateTimers = ({
  channel,
  rights,
  windowStatus,
  scheduledAt,
  shouldCutAtScheduledStart = true,
  miniplayerConfig,
  onStartLivePlayback,
  onStartAlternate,
  onHideMiniPlayer,
  onShowCommercialOffer,
  onRefetchAlternate,
}) => {
  const pollRef = useRef(null);

  // Ref de callbacks para evitar closures stale dentro del setInterval
  const callbacksRef = useRef({});
  callbacksRef.current = {
    onStartLivePlayback,
    onStartAlternate,
    onHideMiniPlayer,
    onShowCommercialOffer,
    onRefetchAlternate,
  };

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!channel || !rights || !windowStatus) return clearPoll;

    const event = channel.events?.[0];
    if (!event) return clearPoll;

    const { dateBegin, dateEnd } = event;
    const scheduledAtMs =
      shouldCutAtScheduledStart && scheduledAt
        ? new Date(scheduledAt).getTime()
        : Infinity;
    const playLiveEnabled = miniplayerConfig.playLiveMatch.enable;

    // ─── FLUJO A — derechos + ventana vigente (WEBCL-2944) ───────────────────
    if (rights === CHANNEL_RIGHTS.HAS_RIGHTS_ACTIVE && windowStatus === WINDOW_STATUS.CURRENT) {
      callbacksRef.current.onStartAlternate(channel);

      const dateEndMs = toUTCDate(dateEnd);
      const endMs = Math.min(dateEndMs, scheduledAtMs);
      const isScheduledFirst = scheduledAtMs < dateEndMs;

      pollRef.current = setInterval(() => {
        const now = Date.now();
        if (now >= endMs) {
          clearPoll();
          if (isScheduledFirst && playLiveEnabled) {
            callbacksRef.current.onStartLivePlayback();
          } else {
            callbacksRef.current.onHideMiniPlayer();
          }
        }
      }, POLL_INTERVAL_MS);

      return clearPoll;
    }

    // ─── FLUJO B — derechos + ventana futura (WEBCL-2947) ───────────────────
    if (rights === CHANNEL_RIGHTS.HAS_RIGHTS_FUTURE && windowStatus === WINDOW_STATUS.FUTURE) {
      const dateBeginMs = toUTCDate(dateBegin);
      const triggerMs = Math.min(dateBeginMs, scheduledAtMs);
      const isScheduledFirst = scheduledAtMs < dateBeginMs;

      pollRef.current = setInterval(() => {
        const now = Date.now();
        if (now >= triggerMs) {
          clearPoll();
          if (isScheduledFirst) {
            if (playLiveEnabled) {
              callbacksRef.current.onStartLivePlayback();
            }
          } else {
            callbacksRef.current.onRefetchAlternate();
          }
        }
      }, POLL_INTERVAL_MS);

      return clearPoll;
    }

    // ─── FLUJO C — sin derechos + ventana vigente (WEBCL-2949) ──────────────
    if (rights === CHANNEL_RIGHTS.NO_RIGHTS && windowStatus === WINDOW_STATUS.CURRENT) {
      callbacksRef.current.onShowCommercialOffer();

      const dateEndMs = toUTCDate(dateEnd);
      const endMs = Math.min(dateEndMs, scheduledAtMs);
      const isScheduledFirst = scheduledAtMs < dateEndMs;

      pollRef.current = setInterval(() => {
        const now = Date.now();
        if (now >= endMs) {
          clearPoll();
          callbacksRef.current.onHideMiniPlayer();
          if (isScheduledFirst && playLiveEnabled) {
            callbacksRef.current.onStartLivePlayback();
          }
        }
      }, POLL_INTERVAL_MS);

      return clearPoll;
    }

    // ─── FLUJO D — sin derechos + ventana futura (WEBCL-2950) ───────────────
    if (rights === CHANNEL_RIGHTS.NO_RIGHTS && windowStatus === WINDOW_STATUS.FUTURE) {
      const dateBeginMs = toUTCDate(dateBegin);
      const triggerMs = Math.min(dateBeginMs, scheduledAtMs);
      const isScheduledFirst = scheduledAtMs < dateBeginMs;

      pollRef.current = setInterval(() => {
        const now = Date.now();
        if (now >= triggerMs) {
          clearPoll();
          if (isScheduledFirst) {
            if (playLiveEnabled) {
              callbacksRef.current.onStartLivePlayback();
            }
          } else {
            callbacksRef.current.onRefetchAlternate();
          }
        }
      }, POLL_INTERVAL_MS);

      return clearPoll;
    }

    return clearPoll;
  }, [
    channel,
    rights,
    windowStatus,
    scheduledAt,
    shouldCutAtScheduledStart,
    miniplayerConfig.playLiveMatch.enable,
  ]);
};
